/**
 * Gemeinsame Bausteine für die selbst ladenden Tabellen-Widgets der
 * Startseite ("Dringend benötigte Betreuungen", "Aufgaben"):
 *
 *  - readWidgetPref / writeWidgetPref — kleine Per-User-Einstellungen
 *    (Einträge pro Seite, "Nur meine Aufgaben") im localStorage, Schlüssel
 *    pro eingeloggtem Admin. localStorage kann fehlen/gesperrt sein → alle
 *    Zugriffe in try/catch, Fallback ist der Default.
 *  - WidgetPager — Fußzeile mit "x pro Seite"-Auswahl und Blättern
 *    (CRM 1.0: Table2-Footer, 10/25/50/100).
 */
import React from 'react';
import { BRAND, getThemeColors } from '../../../../lib/theme';
import { Icon } from '../../../../components/icons';
import { currentAdminId } from '../../../pflege/PbDashboard/helpers';

export const PER_PAGE_OPTIONS = [10, 25, 50, 100] as const;

function prefKey(name: string): string {
  const id = currentAdminId();
  return `sh24.dashboard.${name}.${id ?? 'anon'}`;
}

export function readWidgetPref<T>(name: string, fallback: T, valid: (v: unknown) => v is T): T {
  try {
    const raw = window.localStorage.getItem(prefKey(name));
    if (raw == null) return fallback;
    const parsed: unknown = JSON.parse(raw);
    return valid(parsed) ? parsed : fallback;
  } catch {
    return fallback;
  }
}

export function writeWidgetPref(name: string, value: unknown): void {
  try {
    window.localStorage.setItem(prefKey(name), JSON.stringify(value));
  } catch {
    /* Speicher nicht verfügbar → Einstellung gilt nur für diese Sitzung */
  }
}

export const isPerPage = (v: unknown): v is number =>
  typeof v === 'number' && (PER_PAGE_OPTIONS as readonly number[]).includes(v);

export const isBool = (v: unknown): v is boolean => typeof v === 'boolean';

/**
 * Nächste Sortierung nach Klick auf einen Spaltenkopf — exakt wie CRM 1.0
 * (Table2.sortItems): andere Spalte → aufsteigend; gleiche Spalte wechselt
 * zwischen aufsteigend und absteigend.
 */
export function nextSort(
  current: { field: string | null; order: 0 | 1 | -1 },
  field: string,
): { field: string; order: 1 | -1 } {
  if (current.field !== field) return { field, order: 1 };
  return { field, order: current.order === 1 ? -1 : 1 };
}

interface WidgetPagerProps {
  dark: boolean;
  page: number;
  lastPage: number;
  total: number;
  perPage: number;
  onPage: (page: number) => void;
  onPerPage: (perPage: number) => void;
  tr: (k: string, opts?: Record<string, unknown>) => string;
  /** Optionaler Inhalt links (z.B. Filter-Schalter). */
  children?: React.ReactNode;
}

export function WidgetPager({ dark, page, lastPage, total, perPage, onPage, onPerPage, tr, children }: WidgetPagerProps) {
  const t = getThemeColors(dark);
  const btn = (disabled: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: 7,
    border: `1px solid ${t.border}`, background: t.card,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, padding: 0,
  });
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
      paddingTop: 8, marginTop: 4, borderTop: `1px solid ${t.borderSoft}`,
      fontSize: 11.5, color: t.muted,
    }}>
      {children}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
        <select
          aria-label={tr('dashboard.widget.table.per_page_label')}
          value={perPage}
          onChange={e => onPerPage(Number(e.target.value))}
          style={{
            fontSize: 11.5, padding: '3px 6px', borderRadius: 7,
            border: `1px solid ${t.border}`, background: t.inputBg, color: t.text,
            fontFamily: 'Outfit, sans-serif', cursor: 'pointer',
          }}
        >
          {PER_PAGE_OPTIONS.map(n => (
            <option key={n} value={n}>{tr('dashboard.widget.table.per_page', { count: n })}</option>
          ))}
        </select>
        <span style={{ whiteSpace: 'nowrap' }}>
          {tr('dashboard.widget.table.page_info', { page, pages: Math.max(1, lastPage), total })}
        </span>
        <button
          type="button"
          aria-label={tr('dashboard.widget.table.prev')}
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          style={btn(page <= 1)}
        >
          <Icon.Chevron dir="left" c={page <= 1 ? t.muted : BRAND} />
        </button>
        <button
          type="button"
          aria-label={tr('dashboard.widget.table.next')}
          disabled={page >= lastPage}
          onClick={() => onPage(page + 1)}
          style={btn(page >= lastPage)}
        >
          <Icon.Chevron dir="right" c={page >= lastPage ? t.muted : BRAND} />
        </button>
      </div>
    </div>
  );
}
